import * as React from 'react';
import { useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';
import { useWindowSize } from '@/hooks/useWindowSize';

import {
    ChartsContainer,
    PlatformConditionWidget,
    DonutChartContainer,
    ChartTitle,
    LegendContainer,
    LegendItem,
    LegendDot,
    LegendLabel,
} from './styles';
import { ChartTooltipBox } from './styles';
import { getColorFromConfig } from './utils';

/**
 * Data types for Platform Condition Charts
 */
export type PlatformConditionData = {
    count: number | null | undefined;
    key: string | null | undefined;
    remediated?: number; // Remediated count for this specific condition
};

export type RemediationData = {
    totalRemediated: number;
    totalItems: number;
    percentage: number;
};

type ProcessedSegment = {
    key: string;
    count: number;
    remediated: number;
    color: string;
    startAngle: number;
    endAngle: number;
};

type ProgressSlice = {
    key: string;
    originalKey: string;
    count: number;
    color: string;
    remediatedCount: number;
    totalCount: number;
    isRemediated: boolean;
    startAngle: number;
    endAngle: number;
    innerRadius: number;
    outerRadius: number;
};

export type LegendItemConfig = { key: string; name: string; color: string; isInternal?: boolean };

type PlatformConditionChartsProps = {
    corrosionData?: PlatformConditionData[];
    coatingsData?: PlatformConditionData[];
    corrosionLegendConfig?: LegendItemConfig[];
    coatingsLegendConfig?: LegendItemConfig[];
    corrosionTitle?: string;
    coatingsTitle?: string;
    loading?: boolean;
};

// Default empty data for loading states
const DEFAULT_EMPTY_DATA: PlatformConditionData[] = [];

// Individual donut chart component
interface DonutChartProps {
    title: string;
    data: PlatformConditionData[];
    colorMapper: (key: string) => string;
    legendConfig?: LegendItemConfig[];
    loading?: boolean;
}

const DonutChart: React.FC<DonutChartProps> = ({ title, data, colorMapper, legendConfig = [], loading = false }) => {
    const screenSize = useWindowSize();

    // Chart sizing based on screen size
    const chartSize = useMemo(() => {
        const { width } = screenSize;
        if (width && width <= 1728) {
            return {
                width: 250,
                height: 270,
                outerRadius: 120,
                innerRadius: 100,
                progressOuterRadius: 35,
                progressInnerRadius: 20,
            };
        }
        return {
            width: 300,
            height: 320,
            outerRadius: 120,
            innerRadius: 95,
    }
    return {
      width: 300,
      height: 320,
      outerRadius: 120,
      innerRadius: 95,
      progressOuterRadius: 45,
      progressInnerRadius: 25,
    };
  }, [screenSize]);

  // Use internal metric color from legendConfig (fallback to neutral gray)
  const internalColor = useMemo(() => {
    const internal = legendConfig.find((it) => it.isInternal);
    return internal?.color || '#D3D3D3';
  }, [legendConfig]);


  // Process data for chart
  const processedData: ProcessedSegment[] = useMemo(() => {
    if (!data || data.length === 0 || data.every((item: PlatformConditionData) => item?.count === null || item?.count === 0)) {
      return [{ key: 'N/A', count: 1, color: '#ccc', remediated: 0, startAngle: 0, endAngle: 360 }];
    }

    const filteredData = data.filter((item: PlatformConditionData) => item?.count && item.count > 0);
    const total = filteredData.reduce((sum: number, item: PlatformConditionData) => sum + (item.count || 0), 0);

    // Calculate angles for each segment
    let currentAngle = 0;
    return filteredData.map((item: PlatformConditionData): ProcessedSegment => {
      const count = item.count || 0;
      const segmentAngle = (count / total) * 360;
      const segment: ProcessedSegment = {
        key: item.key || '',
        count,
        remediated: item.remediated || 0,
        color: colorMapper(item.key || ''),
        startAngle: currentAngle,
        endAngle: currentAngle + segmentAngle,
      };
      currentAngle += segmentAngle;
      return segment;
    });
  }, [data, colorMapper]);

  // Process progress data for inner chart - aligned with outer segments
  const totalCount = useMemo(() => {
    return processedData.reduce((sum: number, item: ProcessedSegment) => sum + (item.count || 0), 0) || 1;
  }, [processedData]);

  const progressData: ProgressSlice[] = useMemo(() => {
    if (!processedData || processedData.length === 0) return [];

    // Start from 0 degrees (3 o'clock) and go clockwise
    let currentAngle = 0;
    const segments = processedData.map((item: ProcessedSegment) => {
      const segmentPercentage = ((item.count || 0) / totalCount) * 100;
      const segment = {
        ...item,
        startAngle: currentAngle,
        endAngle: currentAngle + (segmentPercentage * 3.6), // Go clockwise
        percentage: segmentPercentage,
        count: item.count || 0,
        remediated: item.remediated || 0,
        key: item.key || '',
      };
      currentAngle = segment.endAngle; // Update for next segment
      return segment;
    });

    // Create inner segments for remediated portions
    return segments.flatMap((segment) => {
      const { key, count, remediated, startAngle, endAngle } = segment;
      const totalForSegment = count || 0;
      const remediatedCount = remediated || 0;

      if (remediatedCount <= 0 || totalForSegment <= 0) {
        return [] as ProgressSlice[];
      }

      // Calculate the angle for the remediated portion
      const segmentAngleRange = endAngle - startAngle;
      const remediatedAngle = (remediatedCount / totalForSegment) * segmentAngleRange;

      const slices: ProgressSlice[] = [
        {
          key: `${key}_remediated`,
          count: 100,
          color: internalColor, // use internal metric color
          originalKey: key,
          remediatedCount,
          totalCount: totalForSegment,
          isRemediated: true,
          startAngle: startAngle,
          endAngle: startAngle + remediatedAngle,
          innerRadius: chartSize.innerRadius * 0.8,
          outerRadius: chartSize.innerRadius * 0.95,
        },
        {
          key: `${key}_not_remediated`,
          count: 100,
          color: 'transparent',
          originalKey: key,
          remediatedCount: 0,
          totalCount: totalForSegment - remediatedCount,
          isRemediated: false,
          startAngle: startAngle + remediatedAngle,
          endAngle: endAngle,
          innerRadius: chartSize.innerRadius * 0.8,
          outerRadius: chartSize.innerRadius * 0.95,
        },
      ];
      return slices;
    });
  }, [processedData, chartSize.innerRadius, totalCount, internalColor]);

  // Simple tooltip for inner remediated slices only
  const renderInnerTooltip = useCallback((props: TooltipProps<number, string>) => {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) return null;
    const datum = payload[0]?.payload as ProgressSlice | undefined;
    if (!datum || !datum.isRemediated) return null;
    const pct = Math.round((datum.remediatedCount / Math.max(1, datum.totalCount)) * 100);
    const displayName = legendConfig.find((it) => it.key === datum.originalKey)?.name || datum.originalKey;
    return (
      <ChartTooltipBox>
        <div style={{ fontWeight: 600 }}>{displayName}</div>
        <div>{pct}% ({datum.remediatedCount.toFixed(2)}/{datum.totalCount.toFixed(2)})</div>
      </ChartTooltipBox>
    );
  }, [legendConfig]);

  // Create legend items from legend config
  const legendItems: LegendItemConfig[] = useMemo(() => {
    return (legendConfig || []).map((item) => ({
      key: item.key,
      name: item.name,
      color: item.color,
      isInternal: item.isInternal,
    }));
  }, [legendConfig]);

  if (loading) {
    return (
      <PlatformConditionWidget loading>
        <ChartTitle>{title}</ChartTitle>
      </PlatformConditionWidget>
    );
  }

  return (
    <PlatformConditionWidget>
      <ChartTitle>{title}</ChartTitle>

      <DonutChartContainer>
        <Box sx={{ position: 'relative' }}>
          <ResponsiveContainer width={chartSize.width} height={chartSize.height}>
            <PieChart>
              {/* Outer donut chart */}
              <Pie
                data={processedData}
                cx="50%"
                cy="50%"
                innerRadius={chartSize.innerRadius}
                outerRadius={chartSize.outerRadius}
                dataKey="count"
                labelLine={false}
              >
                {processedData.map((entry: ProcessedSegment, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </Pie>

              {/* Inner pie chart - aligned with outer segments */}
              {progressData.map((entry: ProgressSlice, index: number) => (
                <Pie
                  key={`inner-segment-${index}`}
                  data={[entry]}
                  cx="50%"
                  cy="50%"
                  outerRadius={entry.outerRadius}
                  startAngle={entry.startAngle}
                  endAngle={entry.endAngle}
                  paddingAngle={0}
                  dataKey="count"
                >
                  <Cell
                    key={`progress-cell-${index}`}
                    fill={entry.color}
                    style={{ cursor: entry.isRemediated ? 'pointer' : 'default' }}
                    stroke="none"
                  />
                </Pie>
              ))}

              {/* Tooltip for inner slices */}
              <Tooltip content={renderInnerTooltip} />
            </PieChart>
          </ResponsiveContainer>

          {/* Outside percentage labels for outer segments */}
          {processedData.map((entry, index) => {
            const pct = ((entry.count / totalCount) * 100).toFixed(1);
            const midDeg = (entry.startAngle + entry.endAngle) / 2;
            const theta = (-midDeg) * Math.PI / 180;
            const cx = chartSize.width / 2;
            const cy = chartSize.height / 2;
            const baseRadius = chartSize.outerRadius;
            const offset = 18;
            const labelR = baseRadius + offset;
            const x = cx + labelR * Math.cos(theta);
            const y = cy + labelR * Math.sin(theta);

            return (
              <div
                key={`pct-${index}`}
                style={{
                  position: 'absolute',
                  left: `${x}px`,
                  top: `${y}px`,
                  transform: 'translate(-50%, -50%)',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#224063',
                  pointerEvents: 'none',
                  zIndex: 10,
                  whiteSpace: 'nowrap',
                }}
              >
                {pct}%
              </div>
            );
          })}
        </Box>

        {/* Bottom legend */}
        <LegendContainer>
          {legendItems.map((item) => {
            return (
              <LegendItem key={item.key}>
                <LegendDot color={item.color} />
                <LegendLabel>
                  {item.name || item.key}
                </LegendLabel>
              </LegendItem>
            );
          })}
        </LegendContainer>
      </DonutChartContainer>
    </PlatformConditionWidget>
  );
};

// Main component
export const PlatformConditionCharts: React.FC<PlatformConditionChartsProps> = ({
  corrosionData = DEFAULT_EMPTY_DATA,
  coatingsData = DEFAULT_EMPTY_DATA,
  corrosionLegendConfig = [],
  coatingsLegendConfig = [],
  corrosionTitle = 'Platform condition by corrosion',
  coatingsTitle = 'Platform condition by coatings',
  loading = false,
}) => {
 
  return (
    <ChartsContainer direction="row" spacing={3}>
      {corrosionData.length > 0 && (
        <DonutChart
          title={corrosionTitle}
          data={corrosionData}
          colorMapper={(key) => getColorFromConfig(key, corrosionLegendConfig)}
          legendConfig={corrosionLegendConfig}
          loading={loading}
        />
      )}

      {coatingsData.length > 0 && (
        <DonutChart
          title={coatingsTitle}
          data={coatingsData}
          colorMapper={(key) => getColorFromConfig(key, coatingsLegendConfig)}
          legendConfig={coatingsLegendConfig}
          loading={loading}
        />
      )}
    </ChartsContainer>
  );
};
